import {
    BudgetGet,
    BudgetOpenItem,
    Decimal,
    ISubscription,
    SubscribeState,
    Ulid,
} from "@kase/my-marathon-communication";
import { Signal, signal } from "@preact/signals";
import { upsertOne } from "~/utils";
import { ApiSliceParams } from "../interfaces";
import {
    Budget,
    BudgetCreateRequest,
    BudgetLine,
    BudgetLineOpenItem,
    BudgetPhaseOpenItem,
} from "./interfaces";
import { makeLine } from "./line";
import { makePhase } from "./phase";

// @TODO: consider using single store for all budgets like in projects
//
export const makeBudget = ({ connection, company }: ApiSliceParams) => {
    const budgetStore: Record<string, Signal<Budget[]>> = {};
    const loadingStore: Record<string, Signal<boolean>> = {};
    const phase = makePhase({ connection, company });
    const line = makeLine({ connection, company });
    const subscriptions: Record<string, () => void> = {};

    const removeItems = (
        budget: Budget,
        items: readonly Readonly<Omit<BudgetOpenItem, "data">>[],
    ) => {
        const phases = items
            .filter((item) => item.t === "P")
            .map((item) => item.id.toString());

        const newPhases = budget.phases
            .peek()
            .filter((phase) => !phases.includes(phase.id));

        if (newPhases.length !== budget.phases.peek().length) {
            budget.phases.value = newPhases;
        }

        items
            .filter((line) => line.t !== "P")
            .forEach((line) => {
                const phase = budget.phases.value.find((phase) =>
                    phase.lines.value.some((t) => t._id.equals(line.id)),
                );

                if (phase) {
                    phase.lines.value = phase.lines.value.filter(
                        (t) => !t._id.equals(line.id),
                    );
                } else {
                    budget.hangingLines.value =
                        budget.hangingLines.value.filter(
                            (t) => !t._id.equals(line.id),
                        );
                }
            });
    };

    const addItems = (
        budget: Budget,
        items: readonly Readonly<BudgetOpenItem>[],
    ) => {
        const phases = items
            .filter((item): item is BudgetPhaseOpenItem => item.t === "P")
            .map((item) => ({
                ...item,
                _id: item.id,
                id: item.id.toString(),
                lines: signal<BudgetLine[]>([]),
            }));

        if (phases.length) {
            const newPhases = [...budget.phases.peek()];

            phases.forEach((phase) => {
                const existing = newPhases.find((t) => t._id.equals(phase._id));

                if (existing) {
                    Object.assign(existing.data, phase.data);
                } else {
                    newPhases.push(phase);
                }
            });

            budget.phases.value = newPhases.sort(
                (a, b) => (a.data.prio ?? 0) - (b.data.prio ?? 0),
            );
        }

        items
            .filter((line): line is BudgetLineOpenItem => line.t !== "P")
            .forEach((line) => {
                const oldPhase = budget.phases.value.find((phase) =>
                    phase.lines.value.some((t) => t._id.equals(line.id)),
                );
                const phase = budget.phases.value.find((phase) =>
                    phase._id.equals(line.data.phase),
                );

                const finalLine = {
                    ...line,
                    _id: line.id,
                    id: line.id.toString(),
                };

                if (phase) {
                    phase.lines.value = upsertOne(
                        phase.lines.peek().slice(),
                        finalLine,
                        "id",
                    ).sort(
                        (a, b) =>
                            (a.data.position ?? 0) - (b.data.position ?? 0),
                    );

                    if (oldPhase && oldPhase.id !== phase.id) {
                        oldPhase.lines.value = oldPhase.lines.value.filter(
                            (t) => t.id !== finalLine.id,
                        );
                    }
                } else {
                    budget.hangingLines.value = upsertOne(
                        budget.hangingLines.peek().slice(),
                        finalLine,
                        "id",
                    ).sort(
                        (a, b) =>
                            (a.data.position ?? 0) - (b.data.position ?? 0),
                    );

                    if (oldPhase) {
                        oldPhase.lines.value = oldPhase.lines.value.filter(
                            (t) => t.id !== finalLine.id,
                        );
                    }
                }
            });
    };

    const open = async (budget: Budget) => {
        const con = connection.value;
        const sub = con.budget.open({
            company: company.value,
            id: budget._id,
        });

        budget.loading.value = true;

        for await (const event of sub) {
            if (event.kind === SubscribeState.Remove) {
                removeItems(budget, event.values);
            } else {
                addItems(budget, event.values);
            }

            if (event.kind === SubscribeState.Loaded) {
                budget.loading.value = false;
            }
        }
    };

    const view = (budget: BudgetGet) => {
        const finalBudget = {
            ...budget,
            id: budget.id.toString(),
            _id: budget.id,
            phases: signal([]),
            hangingLines: signal([]),
            loading: signal(false),
        };

        open(finalBudget);

        return finalBudget;
    };

    const getStore = (id: string) => {
        if (!(id in budgetStore)) {
            budgetStore[id] = signal([]);
        }

        return budgetStore[id];
    };

    const loadBudget = async (
        budget: Readonly<Ulid> | readonly Readonly<Ulid>[],
    ) => {
        const con = connection.value;
        const comp = company.value;

        if (!con || !comp) {
            return;
        }

        const finalId = Array.isArray(budget) ? budget : [budget];
        const list = con.budget.get({
            id: finalId,
            company: comp,
        });
        const result: Budget[] = [];

        for await (const budget of list) {
            const store = getStore(budget.project.toString());
            const finalBudget = view(budget);

            store.value = [
                ...store.value.filter((t) => t.id !== finalBudget.id),
                finalBudget,
            ];
            result.push(finalBudget);
        }

        return result;
    };

    const loadBudgets = async (id: string) => {
        const con = connection.value;
        const comp = company.value;
        const isLoading = loadingStore[id];

        if (!con || !comp || isLoading.value) {
            return;
        }

        isLoading.value = true;

        const list = con.budget.list({
            company: comp,
            project: Ulid.fromString(id),
        });

        for await (const id of list) {
            await loadBudget(id);
        }

        isLoading.value = false;
    };

    const create = async (request: BudgetCreateRequest) => {
        const con = connection.value;
        const comp = company.value;
        const result = await con.budget.create({
            company: comp,
            header: request.header,
            project: request.project,
            currency: request.currency,
            currency_rate: new Decimal(request.rate),
        });
        let defaultPhaseId: Readonly<Ulid> | null = null;

        if (request.defaultPhase) {
            defaultPhaseId = await phase.create({
                ...request.defaultPhase,
                budget: result,
            });
        }

        const budgets = await loadBudget(result);

        if (!budgets || !budgets[0]) {
            throw new Error("Failed to load created budget");
        }

        return [budgets[0], defaultPhaseId] as const;
    };

    const budgets = (id: string) => {
        if (id in budgetStore) {
            return budgetStore[id];
        }

        budgetStore[id] = signal([]);

        if (!(id in loadingStore)) {
            loadingStore[id] = signal(false);
        }

        loadBudgets(id);

        return budgetStore[id];
    };

    const loading = (id: string) => {
        if (!(id in loadingStore)) {
            loadingStore[id] = signal(false);
        }

        return loadingStore[id];
    };

    const listen = async (
        id: string,
        subscription: ISubscription<Readonly<Ulid>>,
    ) => {
        for await (const event of subscription) {
            if (event.kind === SubscribeState.Remove) {
                const store = getStore(id);

                store.value = store.value.filter(
                    (budget) => !event.values.includes(budget._id),
                );
            } else {
                await loadBudget(event.values);
            }
        }
    };

    const subscribe = (id: string) => {
        if (id in subscriptions) {
            return subscriptions[id];
        }

        const con = connection.value;
        const subscription = con.budget.subscribe({
            company: company.value,
            project: Ulid.fromString(id),
        });

        listen(id, subscription);

        subscriptions[id] = () => subscription.unsub();

        return subscriptions[id];
    };

    return { budgets, loading, create, phase, line, subscribe };
};
